/**
 * Message Processing Utilities for Chat
 *
 * Pure functions for processing conversation messages into UI-friendly formats.
 * Extracted from useChat hook to enable testing.
 */

import type {
   ConversationMessage,
   MessageContentBinary,
   MessageContentBuildWorkflow,
   MessageContentCitation,
   MessageContentFunctionCall,
   MessageContentGeneratedFile,
   MessageContentScriptDraft,
   Role,
} from "@/services/wingman";
import type {CitationSource} from "@/types/ask";
import {upgradeInsecureUrl} from "@/utils/artifactUrl";

// =============================================================================
// Types
// =============================================================================

export interface MessageBuildState {
   pendingSources:
      | {
           id: string;
           fileName?: string;
           mediaType?: string;
           pages?: string[];
           url?: string;
           sourceType?: "web" | "document";
        }[]
      | null;
}

export interface ProcessedMessage {
   text: string;
   isUser: boolean;
   role: Role;
   isAnimated: boolean;
   sources?: MessageBuildState["pendingSources"];
   fileData?:
      | {
           type: "binary";
           mimeType: string;
           data: string;
           /** Remote file location when `data` isn't inlined base64. */
           url?: string;
        }
      | {
           type: "generated";
           mimeType: string;
           fileName: string;
           downloadUrl: string;
           objectPath: string;
        };
   chartData?: {
      type: "pie" | "bar";
      data: any;
   };
   functionCallData?: MessageContentFunctionCall;
   scriptDraftData?: MessageContentScriptDraft;
   buildWorkflowData?: MessageContentBuildWorkflow;
}

export interface MessageProcessingContext {
   /** Translation function for i18n strings */
   t: (key: string, options?: Record<string, unknown>) => string;
   /** Current streaming message ID (null if not streaming) */
   streamingMessageId: string | null;
   /** Function to get file extension from MIME type */
   getFileExtensionFromMimeType: (mimeType: string) => string;
}

// =============================================================================
// Content Type Handlers
// =============================================================================

export const handleTextContent = (
   content: {type: "text"; content: string},
   msg: ConversationMessage,
   state: MessageBuildState | undefined,
   _ctx: Pick<MessageProcessingContext, "streamingMessageId">
): ProcessedMessage | null => {
   const textContent = content;
   if (textContent.content.trim().length > 0) {
      const result: ProcessedMessage = {
         text: textContent.content,
         isUser: msg.role === "user",
         role: msg.role,
         isAnimated: false,
      };
      // Attach sources as soon as they're available (tool results arrive complete)
      if (state?.pendingSources && msg.role === "assistant") {
         result.sources = state.pendingSources;
         state.pendingSources = null;
      }
      return result;
   }
   return null;
};

/** Build a display filename from an optional original name, falling back to a timestamp-based name. */
export const buildFileName = (
   originalName: string | undefined,
   msg: ConversationMessage,
   mimeType: string,
   getExt: (mime: string) => string
): string => {
   if (originalName) return `📎 ${originalName}`;
   const timestamp = (
      typeof msg.timestamp === "string" ? msg.timestamp : msg.timestamp.toISOString()
   )
      .replaceAll(/[:.-]/g, "")
      .slice(0, 15);
   const ext = mimeType ? getExt(mimeType) : "";
   return `📎 file_${timestamp}${ext}`;
};

export const handleBinaryContent = (
   content: Pick<MessageContentBinary, "type" | "data" | "mimeType" | "fileName" | "url">,
   msg: ConversationMessage,
   ctx: Pick<MessageProcessingContext, "getFileExtensionFromMimeType">
): ProcessedMessage => {
   const fileName = buildFileName(content.fileName, msg, content.mimeType, ctx.getFileExtensionFromMimeType);

   return {
      text: fileName,
      isUser: msg.role === "user",
      role: msg.role,
      isAnimated: false,
      fileData: {
         type: "binary",
         mimeType: content.mimeType,
         data: content.data,
         url: content.url,
      },
   };
};

export const handleGeneratedFileContent = (
   content: Pick<
      MessageContentGeneratedFile,
      "type" | "fileName" | "contentType" | "objectPath" | "downloadUrl"
   >,
   msg: ConversationMessage,
   ctx: Pick<MessageProcessingContext, "getFileExtensionFromMimeType">
): ProcessedMessage => {
   const displayName = buildFileName(content.fileName, msg, content.contentType, ctx.getFileExtensionFromMimeType);

   return {
      text: displayName,
      isUser: msg.role === "user",
      role: msg.role,
      isAnimated: false,
      fileData: {
         type: "generated",
         mimeType: content.contentType,
         fileName: content.fileName,
         downloadUrl: upgradeInsecureUrl(content.downloadUrl),
         objectPath: content.objectPath,
      },
   };
};

export const handleDocumentUploadContent = (
   content: {
      type: "document_upload";
      document: {id: string; mediaType: string; fileName?: string};
   },
   msg: ConversationMessage,
   ctx: Pick<MessageProcessingContext, "getFileExtensionFromMimeType">
): ProcessedMessage => {
   const doc = content.document;
   const fileName = buildFileName(doc.fileName, msg, doc.mediaType, ctx.getFileExtensionFromMimeType);

   return {
      text: fileName,
      isUser: msg.role === "user",
      role: msg.role,
      isAnimated: false,
      fileData: {
         type: "binary",
         mimeType: doc.mediaType,
         data: "",
      },
   };
};

export const handleChartContent = (
   content: {type: string; chart?: unknown},
   msg: ConversationMessage,
   chartType: "pie" | "bar"
): ProcessedMessage => {
   // Some legacy/test payloads omit the `chart` wrapper — fall back to the
   // whole content so the chart renderer still receives data.
   const chartData = content.chart ?? content;

   return {
      text: "",
      isUser: msg.role === "user",
      role: msg.role,
      isAnimated: false,
      chartData: {
         type: chartType,
         data: chartData,
      },
   };
};

export const handleFunctionCallContent = (
   content: MessageContentFunctionCall,
   msg: ConversationMessage
): ProcessedMessage => {
   return {
      text: "",
      isUser: msg.role === "user",
      role: msg.role,
      isAnimated: false,
      functionCallData: content,
   };
};

export const handleScriptDraftContent = (
   content: MessageContentScriptDraft,
   msg: ConversationMessage
): ProcessedMessage | null => {
   // A draft without a payload can't render anything meaningful — skip it
   // rather than surfacing a broken card.
   if (!content.draft) return null;
   return {
      text: "",
      isUser: msg.role === "user",
      role: msg.role,
      isAnimated: false,
      scriptDraftData: content,
   };
};

export const handleBuildWorkflowContent = (
   content: MessageContentBuildWorkflow,
   msg: ConversationMessage
): ProcessedMessage | null => {
   // Without a non-empty description there's nothing to seed the builder with
   // (a whitespace-only prompt gets trimmed away later) — skip rather than
   // navigating to an empty build.
   if (!content.workflow?.description?.trim()) return null;
   return {
      text: "",
      isUser: msg.role === "user",
      role: msg.role,
      isAnimated: false,
      buildWorkflowData: content,
   };
};

// --- Helper functions for handleFunctionResultContent ---

const parseRawContent = (raw: unknown): unknown => {
   if (typeof raw === "string") {
      try {
         return JSON.parse(raw);
      } catch {
         return null;
      }
   }
   return typeof raw === "object" ? raw : null;
};

const handleChartFunctionResult = (
   functionContent: any,
   functionName: string,
   msg: ConversationMessage
): ProcessedMessage | null => {
   const isChart = functionName.includes("pie_chart") || functionName.includes("bar_chart");
   if (!isChart) return null;

   const chartType = functionName.includes("pie_chart") ? "pie" : "bar";
   const chartPayload =
      functionContent.result?.chart ??
      functionContent.chart ??
      functionContent.result ??
      functionContent;

   if (!chartPayload || (!chartPayload.data && !chartPayload.title)) return null;

   return {
      text: "",
      isUser: msg.role === "user",
      role: msg.role,
      isAnimated: false,
      chartData: {type: chartType, data: chartPayload},
   };
};

const extractDocumentSources = (results: any[]): MessageBuildState["pendingSources"] => {
   const byId = new Map<
      string,
      {id: string; fileName?: string; mediaType?: string; pages: Set<string>}
   >();

   for (const item of results) {
      const doc = item?.document;
      if (!doc?.id) continue;

      const existing = byId.get(doc.id) ?? {
         id: doc.id,
         fileName: doc.file_name,
         mediaType: doc.media_type,
         pages: new Set<string>(),
      };

      const sp = item?.start_page;
      const ep = item?.end_page;
      if (typeof sp === "number" && typeof ep === "number") {
         existing.pages.add(sp === ep ? String(sp) : `${sp}-${ep}`);
      }
      byId.set(doc.id, existing);
   }

   return Array.from(byId.values()).map((v) => ({
      id: v.id,
      fileName: v.fileName,
      mediaType: v.mediaType,
      pages: Array.from(v.pages),
      sourceType: "document",
   }));
};

const handleDocumentQueryResult = (functionContent: any, state: MessageBuildState): void => {
   const raw = functionContent?.result ?? functionContent;
   const parsed = parseRawContent(raw) as Record<string, unknown> | null;
   const results = parsed?.results;

   if (Array.isArray(results) && results.length > 0) {
      state.pendingSources = extractDocumentSources(results);
   }
};

const isWebSearchFunction = (functionName: string): boolean => {
   if (functionName === "WebSearchPlugin_search") return true;
   const lowerName = functionName?.toLowerCase() ?? "";
   return lowerName.includes("websearch") || lowerName.includes("web_search");
};

const extractWebSources = (results: any[]): any[] => {
   return results
      .map((r: any) => {
         const url: string | undefined = r?.url;
         const title: string | undefined = r?.title;
         if (!url || typeof url !== "string") return null;
         return {id: url, fileName: title || url, url, sourceType: "web" as const};
      })
      .filter(Boolean);
};

const handleWebSearchResult = (functionContent: any, state: MessageBuildState): void => {
   const raw = functionContent?.result ?? functionContent;
   const parsed = parseRawContent(raw) as Record<string, unknown> | null;
   const results = parsed?.results;

   if (!Array.isArray(results) || results.length === 0) return;

   const webSources = extractWebSources(results);
   if (webSources.length > 0) {
      const existing = state.pendingSources ?? [];
      state.pendingSources = [...existing, ...webSources];
   }
};

// --- Main handler ---

export const handleFunctionResultContent = (
   content: any,
   msg: ConversationMessage,
   state: MessageBuildState | undefined
): ProcessedMessage | null => {
   const functionContent = content;
   const functionName: string = functionContent.functionName ?? "";

   // Handle chart function results
   const chartResult = handleChartFunctionResult(functionContent, functionName, msg);
   if (chartResult) return chartResult;

   if (!state) return null;

   // Handle document query results
   if (functionName.includes("document_query")) {
      handleDocumentQueryResult(functionContent, state);
   }

   // Handle web search results
   if (isWebSearchFunction(functionName)) {
      handleWebSearchResult(functionContent, state);
   }

   return null;
};

// =============================================================================
// Source Merging Utilities
// =============================================================================

/**
 * Merge two arrays of sources, deduplicating by id or fileName+url.
 */
export const mergeSources = (a?: any[] | undefined, b?: any[] | undefined): any[] | undefined => {
   const merged = [...(a ?? []), ...(b ?? [])];
   if (merged.length === 0) return undefined;
   const byKey = new Map<string, any>();
   for (const s of merged) {
      const key = s?.id ?? `${s?.fileName ?? ""}-${s?.url ?? ""}`;
      if (!byKey.has(key)) byKey.set(key, s);
   }
   return Array.from(byKey.values());
};

// =============================================================================
// Assistant Message Processing
// =============================================================================

export interface ContentPart {
   type: "text" | "functionCall" | "thinking" | "navigation";
   content?: string;
   data?: any;
   isCompleted?: boolean;
   result?: unknown;
}

interface AssistantMessageResult {
   contentParts: ContentPart[];
   combinedText: string;
   sources: any[] | undefined;
   citations: CitationSource[] | undefined;
}

/**
 * Collect all completed function call IDs and their results from a message's contents.
 */
export const collectCompletedCallIds = (contents: any[]): Set<string> => {
   const completedCallIds = new Set<string>();
   for (const content of contents) {
      if (content.type === "function_result") {
         const resultContent = content as {callId?: string};
         if (resultContent.callId) {
            completedCallIds.add(resultContent.callId);
         }
      }
   }
   return completedCallIds;
};

/**
 * Collect function results mapped by their call IDs.
 */
export const collectFunctionResults = (contents: any[]): Map<string, unknown> => {
   const results = new Map<string, unknown>();
   for (const content of contents) {
      if (content.type === "function_result") {
         const resultContent = content as {callId?: string; result?: unknown};
         if (resultContent.callId) {
            results.set(resultContent.callId, resultContent.result);
         }
      }
   }
   return results;
};

/** Append or merge a thinking content item into the result's contentParts. */
const handleThinkingContent = (
   content: {type: "thinking"; content: string},
   result: AssistantMessageResult
): void => {
   if (content.content.trim().length === 0) return;

   // Merge consecutive thinking parts
   const lastPart = result.contentParts.at(-1);
   if (lastPart?.type === "thinking") {
      lastPart.content = (lastPart.content ?? "") + content.content;
   } else {
      result.contentParts.push({type: "thinking", content: content.content});
   }
};

/** Convert a citation content item and append it to the result's citations list. */
const handleCitationContent = (
   content: MessageContentCitation,
   result: AssistantMessageResult
): void => {
   const citation: CitationSource = {
      citationNumber: content.number,
      sourceType: content.sourceType,
      title: content.title,
      url: content.url,
      fileName: content.fileName,
      mediaType: content.mediaType,
      pages: content.pages,
      callId: content.callId,
   };
   result.citations = result.citations ? [...result.citations, citation] : [citation];
};

/**
 * Process a single content item for an assistant message, updating contentParts and state.
 *
 * `content` is typed as `any` because the upstream message schema uses a loose union;
 * each branch narrows via discriminant checks on `content.type`.
 */
const processAssistantContentItem = (
   content: any,
   msg: ConversationMessage,
   state: MessageBuildState,
   completedCallIds: Set<string>,
   functionResults: Map<string, unknown>,
   result: AssistantMessageResult
): void => {
   if (content.type === "thinking") {
      handleThinkingContent(content as {type: "thinking"; content: string}, result);
   } else if (content.type === "text") {
      const textContent = content as {type: "text"; content: string};
      if (textContent.content.trim().length === 0) return;

      result.contentParts.push({type: "text", content: textContent.content});

      // Build combined text for backwards compatibility
      result.combinedText = result.combinedText
         ? result.combinedText + "\n\n" + textContent.content
         : textContent.content;

      // Attach sources as soon as they're available (tool results arrive complete)
      if (state.pendingSources) {
         result.sources = mergeSources(result.sources, state.pendingSources);
         state.pendingSources = null;
      }
   } else if (content.type === "function_call") {
      const callContent = content as {callId?: string};
      const isCompleted = callContent.callId ? completedCallIds.has(callContent.callId) : false;
      const callResult = callContent.callId ? functionResults.get(callContent.callId) : undefined;
      result.contentParts.push({type: "functionCall", data: content, isCompleted, result: callResult});
   } else if (content.type === "navigation") {
      result.contentParts.push({type: "navigation", data: content});
   } else if (content.type === "function_result") {
      handleFunctionResultContent(content, msg, state);
   } else if (content.type === "citation") {
      handleCitationContent(content as MessageContentCitation, result);
   }
};

/**
 * Process an assistant message's contents into contentParts and combined text.
 */
export const processAssistantContents = (
   msg: ConversationMessage,
   state: MessageBuildState,
   _streamingMessageId: string | null
): AssistantMessageResult => {
   const result: AssistantMessageResult = {
      contentParts: [],
      combinedText: "",
      sources: undefined,
      citations: undefined,
   };

   const completedCallIds = collectCompletedCallIds(msg.contents);
   const functionResults = collectFunctionResults(msg.contents);

   for (const content of msg.contents) {
      processAssistantContentItem(
         content,
         msg,
         state,
         completedCallIds,
         functionResults,
         result
      );
   }

   // If citations exist but the text ContentParts don't have [^N] markers yet
   // (streaming messages — backend only injects markers into persisted text),
   // inject them client-side into the last text part so inline badges render.
   if (result.citations && result.citations.length > 0) {
      const lastTextIdx = result.contentParts.findLastIndex((p) => p.type === "text");
      if (lastTextIdx !== -1) {
         const lastPart = result.contentParts[lastTextIdx];
         if (lastPart.content && !lastPart.content.includes("[^")) {
            const uniqueNumbers = [...new Set(result.citations.map((c) => c.citationNumber))];
            const markers = uniqueNumbers.map((n) => `[^${n}]`).join("");
            result.contentParts[lastTextIdx] = {...lastPart, content: lastPart.content + markers};
         }
      }
   }

   return result;
};

/**
 * Extract special content types (binary, charts) from assistant message contents.
 */
export const extractSpecialContents = (
   msg: ConversationMessage,
   ctx: MessageProcessingContext
): ProcessedMessage[] => {
   const results: ProcessedMessage[] = [];

   for (const content of msg.contents) {
      switch (content.type) {
         case "binary":
            results.push(handleBinaryContent(content, msg, ctx));
            break;
         case "generated_file":
            results.push(handleGeneratedFileContent(content, msg, ctx));
            break;
         case "document_upload":
            results.push(handleDocumentUploadContent(content, msg, ctx));
            break;
         case "pie_chart":
            results.push(handleChartContent(content, msg, "pie"));
            break;
         case "bar_chart":
            results.push(handleChartContent(content, msg, "bar"));
            break;
         case "script_draft": {
            const draftMessage = handleScriptDraftContent(content, msg);
            if (draftMessage) results.push(draftMessage);
            break;
         }
         case "build_workflow": {
            const buildMessage = handleBuildWorkflowContent(content, msg);
            if (buildMessage) results.push(buildMessage);
            break;
         }
      }
   }

   return results;
};

// =============================================================================
// Main Processing Function
// =============================================================================

/**
 * Process a single message content item into a UI-friendly format.
 *
 * @param content - The message content to process
 * @param msg - The parent conversation message
 * @param state - Optional state for tracking pending sources across content items
 * @param ctx - Context with dependencies (translation, streaming state, etc.)
 * @returns Processed message or null if content should be skipped
 */
export const processMessageContent = (
   content: any,
   msg: ConversationMessage,
   state: MessageBuildState | undefined,
   ctx: MessageProcessingContext
): ProcessedMessage | null => {
   switch (content.type) {
      case "text":
         return handleTextContent(content, msg, state, ctx);
      case "binary":
         return handleBinaryContent(content, msg, ctx);
      case "generated_file":
         return handleGeneratedFileContent(content, msg, ctx);
      case "document_upload":
         return handleDocumentUploadContent(content, msg, ctx);
      case "pie_chart":
         return handleChartContent(content, msg, "pie");
      case "bar_chart":
         return handleChartContent(content, msg, "bar");
      case "script_draft":
         return handleScriptDraftContent(content, msg);
      case "build_workflow":
         return handleBuildWorkflowContent(content, msg);
      case "function_call":
         return handleFunctionCallContent(content, msg);
      case "function_result":
         return handleFunctionResultContent(content, msg, state);
      case "token_usage":
      case "notification":
      case "citation":
         return null;
      case "generation_failed":
         // The assistant turn could not be completed (e.g. a Gemini tool-call text leak). Surface the
         // same localized error used for backend errors — never the raw reason code.
         return {
            text: ctx.t("sorryErrorSendingMessage"),
            isUser: false,
            role: "assistant" as const,
            isAnimated: false,
         };
      case "error":
         // Always use translated string — never display raw backend error text to users.
         if (content.message) console.warn("Wingman error content:", content.message);
         return {
            text: ctx.t("sorryErrorSendingMessage"),
            isUser: false,
            role: "assistant" as const,
            isAnimated: false,
         };
      default:
         console.warn(`Unhandled content type found in message ${msg.id}: ${content.type}`);
         return {
            text: ctx.t("unknownContentReceived"),
            isUser: msg.role === "user",
            role: msg.role ?? "assistant",
            isAnimated: false,
         };
   }
};
